import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { googleAuthCallback } from '@/lib/api/auth'
import { useAuthContext } from '@/contexts/auth-context'

const HoomiLogo = '/assets/hoomi-logo.png'

export default function GoogleCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const { login } = useAuthContext()
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    const handleCallback = async () => {
      try {
        const code = searchParams.get('code')
        const error = searchParams.get('error')

        if (error) {
          setStatus('error')
          toast.error(error)
          setTimeout(() => navigate('/auth'), 2000)
          return
        }

        if (!code) {
          setStatus('error')
          toast.error('Authentication failed')
          setTimeout(() => navigate('/auth'), 2000)
          return
        }

        // Exchange code for JWT
        const data = await googleAuthCallback(code)

        // Read flow context saved before the OAuth redirect
        const redirectionUrl = sessionStorage.getItem('sso_redirection_url')
        const platform = sessionStorage.getItem('sso_platform')
        sessionStorage.removeItem('sso_redirection_url')
        sessionStorage.removeItem('sso_platform')

        // Store token and update auth context (sets session_token cookie too)
        login(data.token)
        setStatus('success')

        setTimeout(() => {
          if (redirectionUrl) {
            // Desktop web with redirect
            window.location.href = redirectionUrl
          } else if (platform === 'mobile') {
            // Mobile WebView: pass jwt_token in URL then auto-close
            const params = new URLSearchParams()
            params.set('jwt_token', data.token)
            params.set('platform', 'mobile')
            navigate(`/?${params.toString()}`, { replace: true })
          } else {
            // Desktop no-redirect
            navigate('/authorized', { replace: true })
          }
        }, 1000)
      } catch (error: any) {
        console.error('Google callback error:', error)
        setStatus('error')
        toast.error('Authentication failed. Please try again.')
        setTimeout(() => navigate('/auth'), 2000)
      }
    }

    handleCallback()
  }, [searchParams, navigate])
  
  return (
    <div className="bg-black flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 text-white relative">
      {/* Purple gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0"></div>

      {/* Purple accent elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-500/10 rounded-full blur-3xl"></div>

      {/* Logo */}
      <div className="absolute top-0 left-0 w-full p-4 flex items-center">
        <div className="flex items-center gap-2">
          <img src={HoomiLogo} alt="Hoomi DePIN" width={24} height={24} />
          <span className="font-semibold text-purple-300">Hoomi Social</span>
        </div>
      </div>

      {/* Content */}
      <div className="w-full max-w-md z-10 flex flex-col items-center gap-6">
        {status === 'loading' && (
          <>
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"></div>
            <p className="text-xl text-white">Authenticating with Google...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xl text-white">Authentication successful!</p>
            <p className="text-sm text-white/70">Redirecting to verification...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="h-16 w-16 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-xl text-white">Authentication failed</p>
            <p className="text-sm text-white/70">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  )
}